import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { ReplaySubject, finalize, iif, takeUntil } from 'rxjs';
import { RequestRoot } from 'types';
import { SubscriptionCreationData } from 'src/app/model/subscription-creation-data';
import { SubscriptionCreationResult } from 'src/app/model/subscription-creation-result';
import { DepartmentSelector } from 'src/app/service/department-selector';
import { NavigationService } from 'src/app/service/navigation.service';
import { DialogService } from 'src/app/service/dialog.service';
import { AppService } from 'src/app/app.service';
import { SubscriptionService } from 'src/app/service/subscription.service';
import { InforserDateAdapter } from 'src/app/service/inforser-date-adapter';
import { DateFormattingUtils } from 'src/app/service/date-formatting-utils';
import { PersonNameDictionary, DictionaryPersonName } from 'src/webid/4.0/model/dictionaries/person-name.dictionary';
import { RequestFeatures } from 'src/webid/4.0/model/features';
import { RequestFeaturesV2 } from 'src/webid/4.0/model/features2';
import { RequestDataAccessService } from 'src/webid/4.0/projects/data-access/src/lib/request-data-access.service';
import { environment } from 'src/environments/environment';
import { SmartCaptchaService } from 'src/app/service/smart-captcha.service';
import { SmartCaptchaSubscribeEvent } from 'src/app/types/yandex-captcha';

@Component({
    selector: 'subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: InforserDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: DateFormattingUtils.getDateFormatSettings(),
        },
        SmartCaptchaService,
    ],
})
export class SubscriptionComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('captchaContainer') captchaContainer?: ElementRef<HTMLDivElement>;

    public sequence: string | null = null;
    public birthDate: Date | null = null;
    public lastName: string | null = '';
    public firstName: string | null = '';
    public middleName: string | null = '';
    public requestId: string | null = null;
    public processing: boolean = false;
    public isRequestLoadedFromDatabase: boolean = false;
    public firstNames = PersonNameDictionary.firstNames;
    public middleNames = PersonNameDictionary.middleNames;
    public filteredFirstNames: string[] = [];
    public filteredMiddleNames: string[] = [];
    private dead$ = new ReplaySubject<void>();

    public yandexCaptchaPublicKey = environment.smartCaptchaPublicKey;
    public captchaSolved: boolean = false;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private appService: AppService,
        private dialogService: DialogService,
        private navigationService: NavigationService,
        private subscriptionService: SubscriptionService,
        private requestService: RequestDataAccessService,
        public departmentSelector: DepartmentSelector,
        private readonly smartCaptchaService: SmartCaptchaService
    ) {}

    ngOnInit(): void {
        this.navigationService.activity.setupQuickly('Подписка');
        this.navigationService.activity.loader.show('Инициализация страницы');

        if (this.appService.subscriptionData) {
            this.isRequestLoadedFromDatabase = this.appService.subscriptionData.isApplicationPresent;
            this.lastName = this.appService.subscriptionData.lastName;
            this.firstName = this.appService.subscriptionData.firstName;
            this.middleName = this.appService.subscriptionData.middleName;
            this.birthDate = this.appService.subscriptionData.birthDate;
            this.sequence = this.appService.subscriptionData.sequence;
            this.departmentSelector
                .initialize()
                .pipe(
                    finalize(() => this.navigationService.activity.loader.hide()),
                    takeUntil(this.dead$)
                )
                .subscribe({
                    next: () => {
                        this.departmentSelector.selectedCountry = this.appService.subscriptionData?.departmentCountry ?? null;
                        this.departmentSelector.onSelectedCountryChanged();
                        this.departmentSelector.selectedCity = this.appService.subscriptionData?.departmentCity ?? null;
                        this.departmentSelector.onSelectedCityChanged();
                        this.appService.subscriptionData = null;
                    },
                    error: ({ message }) => {
                        this.appService.subscriptionData = null;
                        this.dialogService.notifyInSnackBar(
                            `Не удалось загрузить список консульских учреждений: ERROR_TEXT: ${message}`,
                            'error'
                        );
                    },
                });
        } else {
            this.departmentSelector
                .initialize()
                .pipe(
                    finalize(() => this.navigationService.activity.loader.hide()),
                    takeUntil(this.dead$)
                )
                .subscribe({
                    error: ({ message }) => {
                        this.dialogService.notifyInSnackBar(
                            `Не удалось загрузить список консульских учреждений: ERROR_TEXT: ${message}`,
                            'error'
                        );
                    },
                });
            this.requestId = this.route.snapshot.queryParams['id'];

            if (this.requestId) {
                const requestRootBuffer = new RequestRoot();
                requestRootBuffer.id = this.requestId;

                this.requestService
                    .load(requestRootBuffer)
                    .pipe(takeUntil(this.dead$))
                    .subscribe({
                        next: (response) => {
                            this.onRequestLoaded(response);
                        },
                        error: ({ message }) => {
                            this.dialogService.notifyInSnackBar(`Не удалось загрузить данные заявления: ERROR_TEXT: ${message}`);
                        },
                    });
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.captchaContainer) {
            this.smartCaptchaService
                .render(this.captchaContainer.nativeElement)
                .pipe(takeUntil(this.dead$))
                .subscribe({
                    next: (response) => {
                        if (response === SmartCaptchaSubscribeEvent.Success) {
                            this.captchaSolved = true;
                        }
                    },
                    error: ({ message }) => {
                        this.dialogService.notifyInSnackBar(message, 'error');
                    },
                });
        } else {
            this.dialogService.notifyInSnackBar('Не найден элемент для отображения виджета капчи. Обратитесь в техподдержку', 'error');
        }
    }

    ngOnDestroy(): void {
        this.dead$.next();
        this.dead$.complete();
    }

    onFirstNameChanged(input: string | null): void {
        this.filteredFirstNames = this.applyNamesFilter(this.firstNames, input || '');
    }

    onMiddleNameChanged(input: string | null): void {
        this.filteredMiddleNames = this.applyNamesFilter(this.middleNames, input || '');
    }

    isValid(): boolean {
        return (
            this.departmentSelector.selectedDepartment !== null &&
            this.birthDate !== null &&
            this.lastName !== '' &&
            this.firstName !== '' &&
            this.middleName !== '' &&
            this.sequence !== null &&
            (this.sequence.length === 25 || (this.sequence.length >= 1 && this.sequence.length <= 8)) &&
            this.captchaSolved
        );
    }

    scanRequestUidBarcode(): void {
        if (this.isRequestLoadedFromDatabase) {
            let extras = {
                queryParams: { id: this.requestId },
                replaceUrl: true,
            };
            this.router.navigate(['/subscription-via-barcode'], extras);
        } else {
            let data = this.createSubscriptionCreationData();
            this.appService.subscriptionData = data;
            this.router.navigate(['/request-uid-scanning']);
        }
    }

    submit(): void {
        const data = this.createSubscriptionCreationData();
        const code = this.departmentSelector?.selectedDepartment?.code;

        if (code && this.sequence) {
            this.processing = true;

            iif(
                () => Boolean(this.requestId),
                this.subscriptionService.subscribeExistingRequest(this.requestId!, code, this.sequence),
                this.subscriptionService.subscribeNewRequest(data)
            ).subscribe({
                next: (response) => {
                    if (response) {
                        this.onSubmitComplete(response);
                    }
                },
                error: (error) => {
                    this.onSubmitFailure(error);
                },
            });
        }
    }

    private createSubscriptionCreationData(): SubscriptionCreationData {
        let data = new SubscriptionCreationData();
        data.lastName = this.lastName;
        data.firstName = this.firstName;
        data.middleName = this.middleName;
        data.birthDate = this.birthDate;
        data.isApplicationPresent = this.isRequestLoadedFromDatabase;
        data.departmentCountry = this.departmentSelector.selectedCountry;
        data.departmentCity = this.departmentSelector.selectedCity;
        data.departmentCode = this.departmentSelector.selectedDepartment?.code ?? null;
        data.sequence = this.sequence;
        return data;
    }

    private onRequestLoaded(r: RequestRoot): void {
        let features = r.features as RequestFeatures | RequestFeaturesV2;
        this.lastName = features.applicant.personIdentity.fullName.lastName;
        this.firstName = features.applicant.personIdentity.fullName.firstName;
        this.middleName = features.applicant.personIdentity.fullName.middleName.value;
        this.birthDate = features.applicant.personIdentity.birthDate.toUTCDate();
        this.departmentSelector.setDepartmentByCode(features.mobileOptions.department, this.dead$).subscribe();
        this.isRequestLoadedFromDatabase = true;
    }

    private onSubmitComplete(result: SubscriptionCreationResult) {
        if (result.success) {
            this.onSubmitSuccess();
        } else {
            this.onSubmitFailure(result.error);
        }
    }

    private onSubmitSuccess() {
        this.dialogService.notifyInSnackBar('Подписка оформлена').subscribe(() => this.navigationService.navigateBack());
    }

    private onSubmitFailure(error: any) {
        let message: string = 'Ошибка обработки данных';
        switch (error) {
            case 'REQUEST_NOT_FOUND':
                message = 'Данные о заявлении отсутствуют';
                break;
            case 'REQUEST_UID_MISSING':
                message = 'Отсутствует UID в запросе верификации';
                break;
            case 'REQUEST_NOT_ACCEPTED':
                message =
                    'Уважаемый заявитель, информация о заявлении из консульского ' +
                    'учреждения пока не поступила. Пожалуйста, повторите попытку позднее.';
                break;
            case 'APPLICANT_BIRTH_DATE_MISSING':
                message = 'Отсутствует дата рождения в запросе верификации';
                break;
            case 'APPLICANT_BIRTHDATE_MISMATCH':
                message = 'Указанный номер справки принадлежит другому заявителю.';
                break;
        }
        let options = { autoclose: false, warn: true };
        this.dialogService.notifyInSnackBar(message, 'error', options);
    }

    private applyNamesFilter(names: DictionaryPersonName[], part: string): string[] {
        return names
            .filter((fn) => fn.russian.startsWith(part))
            .map((fn) => fn.russian)
            .slice(0, 4);
    }
}
